/**
 * ArbeitszeugnisModal.jsx – KI-Arbeitszeugnis-Generator (v2)
 * Schritt 1: Basisdaten + Station + Mitarbeiter-Adresse
 * Schritt 2: Bewertung
 * Schritt 3: KI-Text + Editor + Unterschrift
 * Schritt 4: Download + E-Mail-Versand
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import SignaturePad from './ui/SignaturePad';
import {
  Loader2, FileText, Sparkles, Download, CheckCircle2,
  ChevronRight, AlertCircle, RefreshCw, Building2, User,
  Bold, Italic, Eye, EyeOff, Mail,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Markdown → HTML für Live-Vorschau
function mdToHtml(text) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
}

const NOTE_LABELS = { 1: 'sehr gut', 2: 'gut', 3: 'befriedigend', 4: 'ausreichend', 5: 'mangelhaft' };
const NOTE_COLORS = { 1: 'bg-green-500', 2: 'bg-emerald-400', 3: 'bg-amber-400', 4: 'bg-orange-500', 5: 'bg-red-500' };

function NoteSlider({ label, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm">{label}</Label>
        <span className={`text-xs px-2 py-0.5 rounded-full text-white font-medium ${NOTE_COLORS[value]}`}>
          {value} – {NOTE_LABELS[value]}
        </span>
      </div>
      <div className="flex gap-1">
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button" onClick={() => onChange(n)}
            className={`flex-1 h-8 rounded-lg border-2 text-xs font-bold transition-all ${
              value === n ? `${NOTE_COLORS[n]} text-white border-transparent`
                : 'border-border text-muted-foreground hover:border-primary/40'}`}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

const JOB_PROFILES = [
  { value: 'aushilfe',    label: 'Aushilfe / Minijob',  desc: 'Kasse, Shop, Kundenservice' },
  { value: 'vollzeit',    label: 'Vollzeit / Teilzeit',  desc: 'Kasse, Wareneingang, Schicht' },
  { value: 'schichtleiter', label: 'Schichtleiter',      desc: '+ Teamführung, Verantwortung' },
  { value: 'bistro',      label: 'Bistro-Fachkraft',     desc: 'HACCP, Lebensmittel, Hygiene' },
];

// Automatische Besonderheiten-Vorauswahl je nach Job-Profil
function defaultExtrasForProfil(profil) {
  return {
    fuehrungsverantwortung: profil === 'schichtleiter',
    anzahl_mitarbeiter: profil === 'schichtleiter' ? 2 : null,
    kassenverantwortung: profil === 'aushilfe' || profil === 'vollzeit',
    nachtarbeit: profil === 'schichtleiter',
    haccp: profil === 'bistro',
  };
}

const AUSSCHEIDUNGSGRUENDE = [
  { value: 'auf_eigenen_wunsch', label: 'Auf eigenen Wunsch' },
  { value: 'betriebsbedingt',    label: 'Betriebsbedingt' },
  { value: 'befristung',         label: 'Ende der Befristung' },
  { value: 'aufhebungsvertrag',  label: 'Aufhebungsvertrag' },
  { value: 'sonstiges',          label: 'Sonstiges' },
];

export function ArbeitszeugnisModal({ open, onClose, employee, bogen, locations = [], editingZeugnis = null }) {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const sigRef = useRef(null);
  const isEditMode = !!editingZeugnis;
  const initializedRef = useRef(false); // Verhindert mehrfaches Überschreiben nach Location-Load

  // Im Edit-Modus direkt auf Step 3 (Editor) springen
  const [step, setStep] = useState(isEditMode ? 3 : 1);

  // Schritt 1 – Basisdaten + Firma
  const [form, setForm] = useState({
    mitarbeiter_name: '', mitarbeiter_strasse: '', mitarbeiter_plz_ort: '',
    mitarbeiter_geburtsdatum: '',
    eintrittsdatum: '', austrittsdatum: '',
    taetigkeit: '', beschaeftigung_art: 'Vollzeit',
  });
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [firmaData, setFirmaData] = useState({
    firma_name: '', firma_adresse: '', firma_plz_ort: '',
    firma_inhaber: '', firma_unterzeichner: '', standort: '',
  });

  // Schritt 2 – Bewertung
  const [jobProfil, setJobProfil] = useState('aushilfe');
  const [noten, setNoten] = useState({ leistungsbereitschaft:2, arbeitsbefaehigung:2, fachwissen:2, arbeitsweise:2, sozialverhalten:2 });
  const [extras, setExtras] = useState({ fuehrungsverantwortung:false, anzahl_mitarbeiter:2, kassenverantwortung:false, nachtarbeit:false, haccp:false });
  const [ausscheidungsgrund, setAusscheidungsgrund] = useState('auf_eigenen_wunsch');
  const [zeugnisArt, setZeugnisArt] = useState('endzeugnis'); // endzeugnis | zwischenzeugnis
  const [premiumClaude, setPremiumClaude] = useState(false);

  // Schritt 3 – Ergebnis
  const [generating, setGenerating] = useState(false);
  const [variante1, setVariante1] = useState('');
  const [variante2, setVariante2] = useState('');
  const [selectedVariante, setSelectedVariante] = useState(1);
  const [editedText, setEditedText] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef(null);
  const [modelUsed, setModelUsed] = useState('');
  const [gesamtnote, setGesamtnote] = useState(2);
  const [sigEmpty, setSigEmpty] = useState(true);

  // Schritt 4 – Fertig
  const [saving, setSaving] = useState(false);
  const [savedDoc, setSavedDoc] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      // Modal geschlossen → Init-Flag zurücksetzen für nächste Öffnung
      initializedRef.current = false;
      return;
    }

    if (isEditMode && editingZeugnis) {
      // Edit-Modus: direkt zu Schritt 3 mit bestehendem Text
      setStep(3);
      setEditedText(editingZeugnis.final_text || '');
      setVariante1(editingZeugnis.final_text || '');
      setVariante2('');
      setModelUsed(editingZeugnis.model_used || '');
      setGesamtnote(editingZeugnis.gesamtnote || 2);
      setSavedDoc(null);
      setSaving(false);
      // Firmadaten vorausfüllen
      setFirmaData({
        firma_name: editingZeugnis.firma_name || '',
        firma_adresse: editingZeugnis.firma_adresse || '',
        firma_plz_ort: editingZeugnis.firma_plz_ort || '',
        firma_inhaber: editingZeugnis.firma_inhaber || '',
        firma_unterzeichner: editingZeugnis.firma_unterzeichner || '',
        standort: editingZeugnis.standort || '',
      });
      setForm({
        mitarbeiter_name: editingZeugnis.mitarbeiter_name || '',
        mitarbeiter_strasse: editingZeugnis.mitarbeiter_strasse || '',
        mitarbeiter_plz_ort: editingZeugnis.mitarbeiter_plz_ort || '',
        mitarbeiter_geburtsdatum: editingZeugnis.mitarbeiter_geburtsdatum || '',
        eintrittsdatum: editingZeugnis.eintrittsdatum || '',
        austrittsdatum: editingZeugnis.austrittsdatum || '',
        taetigkeit: editingZeugnis.taetigkeit || '',
        beschaeftigung_art: editingZeugnis.beschaeftigung_art || 'Vollzeit',
      });
      setZeugnisArt(editingZeugnis.zeugnis_art || 'endzeugnis');
      return;
    }

    // ── Neu-Modus: NUR EINMAL initialisieren ──────────────────────────────
    // Verhindert, dass nachträgliches Laden von locations manuelle
    // Eingaben im firma_unterzeichner-Feld überschreibt.
    if (initializedRef.current) return;
    // Warten bis Locations geladen sind (oder direkt wenn leer bleiben)
    if (locations.length === 0 && !employee && !bogen) return;
    initializedRef.current = true;

    setStep(1); setVariante1(''); setVariante2(''); setEditedText('');
    setSavedDoc(null); setGenerating(false); setSaving(false);
    setSigEmpty(true);
    // Extras zurücksetzen (nicht alte Auswahl behalten)
    setExtras(defaultExtrasForProfil('aushilfe'));
    setZeugnisArt('endzeugnis');

    const ag = bogen?.arbeitgeber || {};
    const pe = bogen?.personal || {};
    const be = bogen?.beschaeftigung || {};

    setForm({
      mitarbeiter_name: employee?.name || `${pe.vorname||''} ${pe.familienname||''}`.trim() || '',
      mitarbeiter_strasse: pe.strasse ? `${pe.strasse} ${pe.hausnummer||''}`.trim() : '',
      mitarbeiter_plz_ort: pe.plz ? `${pe.plz} ${pe.ort||''}`.trim() : '',
      mitarbeiter_geburtsdatum: pe.geburtsdatum || '',
      eintrittsdatum: ag.eintrittsdatum
        ? new Date(ag.eintrittsdatum).toLocaleDateString('de-DE') : '',
      austrittsdatum: new Date().toLocaleDateString('de-DE'),
      taetigkeit: ag.ausgeubte_taetigkeit || ag.berufsbezeichnung || '',
      beschaeftigung_art: {vollzeit:'Vollzeit',teilzeit:'Teilzeit',minijob:'Aushilfe (Minijob)'}[ag.arbeitszeit||be.arbeitszeit] || 'Vollzeit',
    });

    const az = ag.arbeitszeit || be.arbeitszeit;
    if (az === 'minijob') setJobProfil('aushilfe');
    else if (az === 'vollzeit') setJobProfil('vollzeit');

    // Standort des Mitarbeiters vorauswählen
    const empLocId = (employee?.location_ids || [])[0] || ag.location_id || bogen?.location_id || '';
    if (empLocId) {
      setSelectedLocationId(empLocId);
      _fillLocationData(empLocId);
    } else if (locations.length === 1) {
      setSelectedLocationId(locations[0].id);
      _fillLocationData(locations[0].id);
    }
  }, [open, employee, bogen, locations]);

  const _fillLocationData = (locId) => {
    const loc = locations.find(l => l.id === locId);
    if (!loc) return;
    setFirmaData({
      firma_name:       loc.legal_name || loc.name || '',
      firma_adresse:    loc.legal_address || '',
      firma_plz_ort:    loc.legal_plz_ort || loc.city || '',
      firma_inhaber:    loc.legal_owner || '',
      firma_unterzeichner: loc.legal_unterzeichner || loc.legal_owner || '',
      standort:         loc.city || loc.name || '',
    });
  };

  // Formatierung auf selektierten Text anwenden (B / I)
  const applyFormat = (marker) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = editedText.slice(start, end);
    if (!selected) {
      // Kein Text markiert → Marker einfügen und Cursor dazwischen setzen
      const newText = editedText.slice(0, start) + marker + marker + editedText.slice(start);
      setEditedText(newText);
      setTimeout(() => { el.focus(); el.setSelectionRange(start + marker.length, start + marker.length); }, 0);
      return;
    }
    const formatted = `${marker}${selected}${marker}`;
    const newText = editedText.slice(0, start) + formatted + editedText.slice(end);
    setEditedText(newText);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + marker.length, end + marker.length); }, 0);
  };

  const handleLocationChange = (locId) => {
    setSelectedLocationId(locId);
    _fillLocationData(locId);
  };

  const setNote = (field, val) => setNoten(n => ({...n, [field]: val}));
  const gesamtNote = Math.round(Object.values(noten).reduce((a,b)=>a+b,0)/Object.values(noten).length);

  // ── KI generieren ─────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setGenerating(true);
    setStep(3);
    try {
      const res = await axios.post(`${API}/api/arbeitszeugnis/generate`, {
        employee_id: employee?.id,
        bogen_id: bogen?.id,
        ...form,
        firma_name: firmaData.firma_name || form.mitarbeiter_name,
        standort: firmaData.standort,
        job_profil: jobProfil,
        ...noten,
        ...extras,
        ausscheidungsgrund,
        zeugnis_art: zeugnisArt,
        premium_claude: premiumClaude,
      }, { headers });
      setVariante1(res.data.variante1 || '');
      setVariante2(res.data.variante2 || '');
      setEditedText(res.data.variante1 || '');
      setSelectedVariante(1);
      setModelUsed(res.data.model_used || '');
      setGesamtnote(res.data.gesamtnote || 2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'KI-Generierung fehlgeschlagen');
      setStep(2);
    } finally { setGenerating(false); }
  };

  const handleSelectVariante = (v) => {
    setSelectedVariante(v);
    setEditedText(v === 1 ? variante1 : variante2);
  };

  // ── Speichern ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!editedText.trim()) { toast.error('Kein Zeugnistext vorhanden'); return; }
    if (sigRef.current?.isEmpty()) { toast.error('Bitte zuerst unterschreiben'); return; }

    const sigData = sigRef.current ? sigRef.current.toDataURL('image/png') : null;
    setSaving(true);
    try {
      let res;
      if (isEditMode && editingZeugnis) {
        // Edit-Modus: bestehenden Eintrag überschreiben (PUT)
        res = await axios.put(`${API}/api/arbeitszeugnis/${editingZeugnis.id}`, {
          ...form,
          ...firmaData,
          final_text: editedText,
          signature_data: sigData,
          ausscheidungsgrund,
          gesamtnote,
          job_profil: jobProfil,
        }, { headers });
        toast.success('Arbeitszeugnis aktualisiert!');
      } else {
        // Neu anlegen (POST)
        res = await axios.post(`${API}/api/arbeitszeugnis`, {
          employee_id: employee?.id,
          bogen_id: bogen?.id,
          location_id: selectedLocationId || undefined,
          ...form,
          ...firmaData,
          final_text: editedText,
          model_used: modelUsed,
          job_profil: jobProfil,
          gesamtnote,
          ausscheidungsgrund,
          zeugnis_art: zeugnisArt,
          signature_data: sigData,
        }, { headers });
        toast.success('Arbeitszeugnis gespeichert!');
      }
      setSavedDoc(res.data);
      setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  // ── PDF Download ──────────────────────────────────────────────────────────
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState('');
  const [recipientEmail, setRecipientEmail] = useState(
    employee?.email || bogen?.personal?.email || editingZeugnis?.mitarbeiter_email || ''
  );

  const handleSendEmail = async () => {
    const zeugnisId = savedDoc?.id || editingZeugnis?.id;
    if (!zeugnisId) {
      toast.error('Kein gespeichertes Zeugnis gefunden');
      return;
    }
    setSendingEmail(true);
    try {
      const email = (recipientEmail || '').trim();
      if (!email || !email.includes('@') || !email.split('@')[1]?.includes('.')) {
        toast.error('Bitte eine gültige Empfänger-E-Mail eingeben');
        setSendingEmail(false);
        return;
      }
      const res = await axios.post(
        `${API}/api/arbeitszeugnis/${zeugnisId}/send-email`,
        { email },
        { headers }
      );
      setEmailSentTo(res.data.sent_to);
      toast.success(`✅ Arbeitszeugnis erfolgreich an ${res.data.sent_to} gesendet!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'E-Mail-Versand fehlgeschlagen');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownload = async () => {    if (!savedDoc?.id) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/api/arbeitszeugnis/${savedDoc.id}/pdf`, {
        headers, responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Arbeitszeugnis_${form.mitarbeiter_name.replace(/ /g, '_')}.pdf`;
      a.click();
    } catch { toast.error('PDF-Download fehlgeschlagen'); }
    finally { setDownloading(false); }
  };

  const handleClose = () => {
    setStep(1); setSavedDoc(null); setGenerating(false); setSaving(false);
    onClose();
  };

  // PIN-Schutz für Arbeitszeugnisse entfernt – PDF ist frei öffenbar (Weitergabe an neue Arbeitgeber).

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-violet-600" />
            {isEditMode ? 'Arbeitszeugnis bearbeiten' : 'KI-Arbeitszeugnis'}
            <span className="ml-auto text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {isEditMode ? 'Bearbeiten' : `Schritt ${step} / 4`}
            </span>
          </DialogTitle>
          <div className="flex gap-1 mt-2">
            {[1,2,3,4].map(s => (
              <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${
                s < step ? 'bg-green-500' : s === step ? 'bg-violet-500' : 'bg-muted'}`} />
            ))}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* ── SCHRITT 1: Basisdaten ── */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Zeugnisart */}
              <div className="border border-primary/30 bg-primary/5 rounded-xl p-4 space-y-2">
                <p className="text-xs font-semibold text-primary uppercase tracking-wide">Zeugnisart</p>
                <div className="grid grid-cols-2 gap-2">
                  {[['endzeugnis','Endzeugnis','beim Austritt'],['zwischenzeugnis','Zwischenzeugnis','laufendes Arbeitsverhältnis']].map(([val,label,desc]) => (
                    <button key={val} type="button" onClick={() => setZeugnisArt(val)}
                      className={`text-left p-3 rounded-xl border-2 transition-all ${zeugnisArt === val ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30' : 'border-border hover:border-violet-300'}`}
                      data-testid={`zeugnisart-${val}`}>
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground">{desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              {/* Station auswählen */}
              {locations.length > 0 && (
                <div className="border border-primary/30 bg-primary/5 rounded-xl p-4 space-y-3">
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5" /> Station / Firmenprofil
                  </p>
                  <select value={selectedLocationId} onChange={e => handleLocationChange(e.target.value)}
                    className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                    data-testid="zeugnis-location-select">
                    <option value="">Station auswählen…</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.legal_name || loc.name}{loc.legal_name && loc.name && loc.legal_name !== loc.name ? ` (${loc.name})` : ''}
                      </option>
                    ))}
                  </select>
                  {selectedLocationId && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Firmenname</Label>
                        <Input value={firmaData.firma_name} className="h-8 text-sm"
                          onChange={e => setFirmaData(f => ({...f, firma_name: e.target.value}))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Unterzeichner / Titel</Label>
                        <Input value={firmaData.firma_unterzeichner} placeholder="Ali Tombul, Geschäftsführer"
                          className="h-8 text-sm"
                          onChange={e => setFirmaData(f => ({...f, firma_unterzeichner: e.target.value}))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Straße</Label>
                        <Input value={firmaData.firma_adresse} placeholder="z.B. Stuttgarter Str. 1"
                          className="h-8 text-sm"
                          onChange={e => setFirmaData(f => ({...f, firma_adresse: e.target.value}))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">PLZ / Ort</Label>
                        <Input value={firmaData.firma_plz_ort} placeholder="70734 Fellbach"
                          className="h-8 text-sm"
                          onChange={e => setFirmaData(f => ({...f, firma_plz_ort: e.target.value}))} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Mitarbeiter-Daten */}
              <div className="border border-border rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" /> Mitarbeiter
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1">
                    <Label className="text-sm">Name *</Label>
                    <Input value={form.mitarbeiter_name} onChange={e => setForm(f => ({...f, mitarbeiter_name: e.target.value}))}
                      placeholder="Lara Altmann" data-testid="zeugnis-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Straße <span className="text-muted-foreground text-xs">(optional)</span></Label>
                    <Input value={form.mitarbeiter_strasse} onChange={e => setForm(f => ({...f, mitarbeiter_strasse: e.target.value}))}
                      placeholder="Musterstr. 5" className="h-9 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">PLZ / Ort <span className="text-muted-foreground text-xs">(optional)</span></Label>
                    <Input value={form.mitarbeiter_plz_ort} onChange={e => setForm(f => ({...f, mitarbeiter_plz_ort: e.target.value}))}
                      placeholder="70734 Fellbach" className="h-9 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Geburtsdatum</Label>
                    <Input value={form.mitarbeiter_geburtsdatum}
                      onChange={e => setForm(f => ({...f, mitarbeiter_geburtsdatum: e.target.value}))}
                      placeholder="13.11.2004" className="h-9 text-sm" />
                  </div>
                </div>
              </div>

              {/* Beschäftigungsdaten */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Eintrittsdatum *</Label>
                  <Input value={form.eintrittsdatum} onChange={e => setForm(f => ({...f, eintrittsdatum: e.target.value}))}
                    placeholder="01.04.2026" data-testid="zeugnis-eintritt" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">{zeugnisArt === 'zwischenzeugnis' ? 'Ausstellungsdatum' : 'Austrittsdatum *'}</Label>
                  <Input value={form.austrittsdatum} onChange={e => setForm(f => ({...f, austrittsdatum: e.target.value}))}
                    placeholder={zeugnisArt === 'zwischenzeugnis' ? 'z.B. heute' : '31.12.2026'} data-testid="zeugnis-austritt" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-sm">Ausgeübte Tätigkeit</Label>
                  <Input value={form.taetigkeit} onChange={e => setForm(f => ({...f, taetigkeit: e.target.value}))}
                    placeholder="z.B. Kassierer / Verkäufer" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Beschäftigungsart</Label>
                  <select value={form.beschaeftigung_art} onChange={e => setForm(f => ({...f, beschaeftigung_art: e.target.value}))}
                    className="h-9 w-full rounded-md border bg-background px-3 text-sm">
                    {['Vollzeit','Teilzeit','Aushilfe (Minijob)'].map(v => <option key={v}>{v}</option>)}
                  </select>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={() => setStep(2)}
                  disabled={!form.mitarbeiter_name || !form.eintrittsdatum || (zeugnisArt !== 'zwischenzeugnis' && !form.austrittsdatum)}
                  data-testid="zeugnis-step1-next">
                  Weiter zur Bewertung <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* ── SCHRITT 2: Bewertung ── */}
          {step === 2 && (
            <div className="space-y-5">
              {/* Job-Profil */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Job-Profil</Label>
                <div className="grid grid-cols-2 gap-2">
                  {JOB_PROFILES.map(opt => (
                    <button key={opt.value} type="button" onClick={() => {
                        setJobProfil(opt.value);
                        setExtras(defaultExtrasForProfil(opt.value));
                      }}
                      className={`text-left p-3 rounded-xl border-2 transition-all ${
                        jobProfil === opt.value ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30'
                          : 'border-border hover:border-violet-300'}`}
                      data-testid={`profil-${opt.value}`}>
                      <p className="text-sm font-medium">{opt.label}</p>
                      <p className="text-xs text-muted-foreground">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Noten */}
              <div className="space-y-3 border-t pt-4">
                <p className="text-sm font-semibold">Leistungsbewertung</p>
                <NoteSlider label="Leistungsbereitschaft (Einsatz, Motivation)" value={noten.leistungsbereitschaft} onChange={v => setNote('leistungsbereitschaft', v)} />
                <NoteSlider label="Arbeitsbefähigung (Belastbarkeit, Auffassungsgabe)" value={noten.arbeitsbefaehigung} onChange={v => setNote('arbeitsbefaehigung', v)} />
                <NoteSlider label="Fachwissen (Kasse, Shop, Bistro)" value={noten.fachwissen} onChange={v => setNote('fachwissen', v)} />
                <NoteSlider label="Arbeitsweise (Sorgfalt, Zuverlässigkeit)" value={noten.arbeitsweise} onChange={v => setNote('arbeitsweise', v)} />
                <NoteSlider label="Sozialverhalten (Kollegen, Kunden, Vorgesetzte)" value={noten.sozialverhalten} onChange={v => setNote('sozialverhalten', v)} />
                <div className="flex items-center justify-between bg-muted/40 rounded-lg px-3 py-2">
                  <span className="text-sm font-semibold">Gesamtnote:</span>
                  <span className={`text-sm font-bold px-3 py-1 rounded-full text-white ${NOTE_COLORS[gesamtNote]}`}>
                    {gesamtNote} – {NOTE_LABELS[gesamtNote]}
                  </span>
                </div>
              </div>

              {/* Extras */}
              <div className="space-y-2 border-t pt-4">
                <p className="text-sm font-semibold">Besonderheiten</p>
                <div className="grid grid-cols-2 gap-2">
                  {[['kassenverantwortung','Kassenverantwortung'],['nachtarbeit','Nacht-/Frühdienst'],
                    ['haccp','HACCP / Lebensmittelhygiene'],['fuehrungsverantwortung','Führungsverantwortung']].map(([key,label]) => (
                    <div key={key} onClick={() => setExtras(e => ({...e, [key]: !e[key]}))}
                      className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer text-sm transition-all ${
                        extras[key] ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300'
                          : 'border-border text-muted-foreground'}`}>
                      <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 ${extras[key] ? 'border-violet-500 bg-violet-500' : 'border-muted-foreground'}`}>
                        {extras[key] && <CheckCircle2 className="h-3 w-3 text-white" />}
                      </div>
                      {label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Ausscheidungsgrund + Modell */}
              <div className={`grid gap-3 border-t pt-4 ${zeugnisArt === 'zwischenzeugnis' ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {zeugnisArt !== 'zwischenzeugnis' && (
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold">Ausscheidungsgrund</Label>
                  <select value={ausscheidungsgrund} onChange={e => setAusscheidungsgrund(e.target.value)}
                    className="h-9 w-full rounded-md border bg-background px-3 text-sm">
                    {AUSSCHEIDUNGSGRUENDE.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold">KI-Modell</Label>
                  <div onClick={() => setPremiumClaude(v => !v)}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer text-xs transition-all h-9 ${
                      premiumClaude ? 'border-violet-400 bg-violet-50 dark:bg-violet-950/30' : 'border-border'}`}>
                    <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 ${premiumClaude ? 'border-violet-500 bg-violet-500' : 'border-muted-foreground'}`}>
                      {premiumClaude && <CheckCircle2 className="h-3 w-3 text-white" />}
                    </div>
                    <span>{premiumClaude ? 'Claude Sonnet (~2 Cent)' : 'Gemini Flash (~0,5 Cent)'}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-between pt-2">
                <Button variant="outline" onClick={() => setStep(1)}>Zurück</Button>
                <Button onClick={handleGenerate} className="gap-2 bg-violet-600 hover:bg-violet-700"
                  data-testid="generate-zeugnis-btn">
                  <Sparkles className="h-4 w-4" />
                  {premiumClaude ? 'Premium (Claude)' : 'Zeugnis generieren'}
                </Button>
              </div>
            </div>
          )}

          {/* ── SCHRITT 3: KI-Text + Unterschrift ── */}
          {step === 3 && (
            <div className="space-y-4">
              {generating ? (
                <div className="flex flex-col items-center justify-center py-16 space-y-4">
                  <div className="w-16 h-16 rounded-2xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
                  </div>
                  <p className="text-sm font-medium">KI schreibt das Zeugnis...</p>
                  <p className="text-xs text-muted-foreground">{premiumClaude ? 'Claude Sonnet' : 'Gemini Flash'} · Deutsches Zeugnisrecht</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-xs bg-muted/40 rounded-lg px-3 py-2">
                    <Sparkles className="h-3.5 w-3.5 text-violet-600" />
                    <span>Generiert mit <strong>{modelUsed}</strong> · Gesamtnote: <strong>{gesamtNote} – {NOTE_LABELS[gesamtNote]}</strong></span>
                  </div>

                  {variante2 && (
                    <div className="flex gap-2">
                      {[1,2].map(v => (
                        <button key={v} type="button" onClick={() => handleSelectVariante(v)}
                          className={`flex-1 py-2 text-sm rounded-lg border-2 font-medium transition-all ${
                            selectedVariante === v ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30 text-violet-700'
                              : 'border-border text-muted-foreground'}`}>
                          Variante {v}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold">Text bearbeiten</Label>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={handleGenerate}
                          className="flex items-center gap-1 text-xs text-violet-600 hover:underline">
                          <RefreshCw className="h-3 w-3" /> Neu generieren
                        </button>
                      </div>
                    </div>

                    {/* Formatierungs-Toolbar */}
                    <div className="flex items-center gap-1 p-1.5 bg-muted/40 rounded-lg border border-border">
                      <button
                        type="button"
                        onMouseDown={e => { e.preventDefault(); applyFormat('**'); }}
                        title="Fett (Text markieren, dann klicken)"
                        className="p-1.5 rounded hover:bg-background font-bold text-sm w-8 h-8 flex items-center justify-center transition-colors"
                      ><Bold className="h-3.5 w-3.5" /></button>
                      <button
                        type="button"
                        onMouseDown={e => { e.preventDefault(); applyFormat('*'); }}
                        title="Kursiv (Text markieren, dann klicken)"
                        className="p-1.5 rounded hover:bg-background italic text-sm w-8 h-8 flex items-center justify-center transition-colors"
                      ><Italic className="h-3.5 w-3.5" /></button>
                      <div className="w-px h-5 bg-border mx-1" />
                      <button
                        type="button"
                        onClick={() => setShowPreview(v => !v)}
                        title="Vorschau ein/aus"
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${showPreview ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700' : 'hover:bg-background text-muted-foreground'}`}
                      >
                        {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        {showPreview ? 'Editor' : 'Vorschau'}
                      </button>
                      <span className="ml-auto text-[10px] text-muted-foreground hidden sm:block">
                        Text markieren → <strong>B</strong> oder <em>I</em> klicken
                      </span>
                    </div>

                    {showPreview ? (
                      /* Live-Vorschau mit gerendertem Markdown */
                      <div
                        className="w-full min-h-[220px] rounded-xl border bg-white dark:bg-gray-900 px-4 py-3 text-sm leading-relaxed text-gray-900 dark:text-gray-100 overflow-auto"
                        dangerouslySetInnerHTML={{ __html: mdToHtml(editedText) }}
                      />
                    ) : (
                      /* Editor */
                      <textarea
                        ref={textareaRef}
                        value={editedText}
                        onChange={e => setEditedText(e.target.value)}
                        rows={12}
                        className="w-full rounded-xl border bg-background px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none font-mono"
                        data-testid="zeugnis-editor"
                        placeholder="Zeugnis-Text… Text markieren und B/I klicken für Formatierung"
                      />
                    )}
                    <p className="text-[10px] text-muted-foreground">
                      <strong>**fett**</strong> · <em>*kursiv*</em> · wird im PDF korrekt gerendert
                    </p>
                  </div>

                  {(editedText.includes('bemüht') || editedText.includes('versuchte')) && (
                    <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <p><strong>Achtung:</strong> "bemüht" oder "versuchte" sind Zeugniscode für schlechte Noten!</p>
                    </div>
                  )}

                  {/* Unterschrift */}
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold">Unterschrift Arbeitgeber / Geschäftsführer *</Label>
                      <button type="button" onClick={() => { sigRef.current?.clear(); setSigEmpty(true); }}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                        <RefreshCw className="h-3 w-3" /> Zurücksetzen
                      </button>
                    </div>
                    <div className={`border-2 rounded-xl overflow-hidden bg-white dark:bg-gray-900 ${sigEmpty ? 'border-amber-300 dark:border-amber-700' : 'border-green-400 dark:border-green-700'}`}>
                      <SignaturePad
                        ref={sigRef}
                        penColor="#1E40AF"
                        height={160}
                        onEnd={() => setSigEmpty(false)}
                      />
                    </div>
                    {sigEmpty && <p className="text-xs text-amber-600 dark:text-amber-400">Bitte Unterschrift zeichnen um fortzufahren</p>}
                  </div>

                  <div className="flex gap-2 justify-between">
                    <Button variant="outline" onClick={() => setStep(2)}>Zurück</Button>
                    <Button onClick={handleSave}
                      disabled={saving || !editedText.trim() || sigEmpty}
                      className="gap-2 bg-violet-600 hover:bg-violet-700"
                      data-testid="save-zeugnis-btn">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Zeugnis fertigstellen
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── SCHRITT 4: Fertig ── */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-5 text-center space-y-2">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
                <p className="font-semibold text-green-800 dark:text-green-300">Zeugnis fertiggestellt!</p>
                <p className="text-sm text-green-700 dark:text-green-400">
                  Mit Ihrer Unterschrift und in der Personalakte archiviert.
                </p>
              </div>

              <Button onClick={handleDownload} disabled={downloading} size="lg" className="w-full gap-2 bg-violet-600 hover:bg-violet-700"
                data-testid="download-zeugnis-pdf">
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                PDF herunterladen
              </Button>

              {/* E-Mail senden */}
              {emailSentTo ? (
                <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-4 py-3 text-sm text-green-700 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  <span>Gesendet an <strong>{emailSentTo}</strong></span>
                </div>
              ) : (
                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Empfänger-E-Mail</label>
                    <Input
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="name@beispiel.de"
                      data-testid="zeugnis-recipient-email-input"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Vorbefüllt mit der hinterlegten Adresse – bei Bedarf ändern.
                    </p>
                  </div>
                  <Button
                    onClick={handleSendEmail}
                    disabled={sendingEmail}
                    variant="outline"
                    size="lg"
                    className="w-full gap-2 border-violet-300 text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-900/20"
                    data-testid="send-zeugnis-email-btn"
                  >
                    {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                    Per E-Mail senden
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {step < 4 && step !== 3 && (
          <div className="px-6 py-2 border-t bg-muted/20 shrink-0">
            <p className="text-xs text-muted-foreground text-center">
              Credits automatisch vom Universal Key · Standard ~0,5 Cent · Premium ~2 Cent
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
